// src/pages/Auth/SignupPage.tsx - FIXED VERSION
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff, Mail, Lock, User, UserPlus, Check, X } from 'lucide-react';
import { userActions } from '../../store/reducers/userReducers';
import { uiActions } from '../../store/reducers/uiReducers';
import { googleAuthService } from '../../services/googleAuth';
import unifiedAuth from '../../utils/unifiedAuth';

const SignupPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const missing: string[] = [];
      if (formData.password.length < 8) missing.push('at least 8 characters');
      if (!/[A-Z]/.test(formData.password)) missing.push('one uppercase letter (A-Z)');
      if (!/[a-z]/.test(formData.password)) missing.push('one lowercase letter (a-z)');
      if (!/\d/.test(formData.password)) missing.push('one digit (0-9)');
      if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) missing.push('one special character (!@#$%^&*...)');
      if (missing.length > 0) {
        newErrors.password = 'Password must contain: ' + missing.join(', ');
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      // Use unifiedAuth register method
      const response = await unifiedAuth.auth.register({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
      });

      // Show success toast
      dispatch(uiActions.addNotification({
        message: 'Account created successfully! Please check your email to verify your account.',
        type: 'success'
      }));

      // Navigate to verification page
      navigate('/verify-email', { 
        state: { 
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          message: response.message
        }
      });

    } catch (error: unknown) {
      console.error('Signup error:', error);
      const err = error as Error & { fieldErrors?: { field: string; message: string }[] };
      if (err.fieldErrors && err.fieldErrors.length > 0) {
        const fieldMap: { [key: string]: string } = {};
        for (const fe of err.fieldErrors) {
          // Map backend field names to frontend form field names
          const fieldKey = fe.field === 'passwordConfirm' ? 'confirmPassword' : fe.field;
          fieldMap[fieldKey] = fe.message;
        }
        setErrors(fieldMap);
      } else {
        setErrors({ submit: err.message || 'Signup failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const googleBtnRef = useRef<HTMLDivElement>(null);

  const handleGoogleCredential = useCallback(async (googleUser: { email: string; first_name: string; last_name: string; google_id: string; avatar_url?: string }) => {
    try {
      setIsLoading(true);
      setErrors({});

      const response = await unifiedAuth.auth.googleSignup({
        email: googleUser.email,
        first_name: googleUser.first_name,
        last_name: googleUser.last_name,
        google_id: googleUser.google_id,
        avatar_url: googleUser.avatar_url,
      });

      if (response.user) {
        dispatch(userActions.setUserInfo(response.user));
      }

      navigate('/');
    } catch (error: unknown) {
      console.error('Google signup error:', error);
      const err = error as Error;
      if (err.message && err.message.includes('already exists')) {
        setErrors({ submit: 'An account with this Google email already exists. Please log in instead.' });
      } else {
        setErrors({ submit: err.message || 'Google signup was unsuccessful. Please try again or sign up with email.' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!googleBtnRef.current) return;
    let cancelled = false;

    googleAuthService.renderButton(googleBtnRef.current).then((user) => {
      if (!cancelled) handleGoogleCredential(user);
    }).catch((err) => {
      if (!cancelled && err.message !== 'Sign-in cancelled') {
        console.error('Google signup error:', err);
        if (err.message && err.message.includes('already exists')) {
          setErrors({ submit: 'An account with this Google email already exists. Please log in instead.' });
        } else {
          setErrors({ submit: err.message || 'Google signup was unsuccessful. Please try again or sign up with email.' });
        }
      }
    });

    return () => {
      cancelled = true;
      googleAuthService.cancel();
    };
  }, [handleGoogleCredential]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="text-3xl logo-text font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent text-glow-hover">
            _strymhub
          </Link>
          
          <div className="mt-8 flex justify-center">
            <div className="bg-orange-500/20 rounded-full p-3">
              <UserPlus className="h-8 w-8 text-orange-400" />
            </div>
          </div>
          
          <h2 className="mt-6 text-3xl font-bold text-white">
            Join StrymHub
          </h2>
          <p className="mt-2 text-gray-400">
            Create your account to start streaming
          </p>
        </div>

        {/* Signup Form */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-400 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* First Name and Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`
                      block w-full pl-10 pr-3 py-3 border rounded-lg bg-slate-800/50 text-white
                      placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500
                      ${errors.firstName ? 'border-red-500' : 'border-gray-600 focus:border-orange-500'}
                    `}
                    placeholder="First name"
                  />
                </div>
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`
                    block w-full px-3 py-3 border rounded-lg bg-slate-800/50 text-white
                    placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500
                    ${errors.lastName ? 'border-red-500' : 'border-gray-600 focus:border-orange-500'}
                  `}
                  placeholder="Last name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`
                    block w-full pl-10 pr-3 py-3 border rounded-lg bg-slate-800/50 text-white
                    placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500
                    ${errors.email ? 'border-red-500' : 'border-gray-600 focus:border-orange-500'}
                  `}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`
                    block w-full pl-10 pr-10 py-3 border rounded-lg bg-slate-800/50 text-white
                    placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500
                    ${errors.password ? 'border-red-500' : 'border-gray-600 focus:border-orange-500'}
                  `}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password}</p>
              )}
              {/* Password Requirements Checklist */}
              {formData.password && (
                <div className="mt-2 space-y-1">
                  {[
                    { label: 'At least 8 characters', met: formData.password.length >= 8 },
                    { label: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(formData.password) },
                    { label: 'One lowercase letter (a-z)', met: /[a-z]/.test(formData.password) },
                    { label: 'One digit (0-9)', met: /\d/.test(formData.password) },
                    { label: 'One special character (!@#$%^&*...)', met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password) },
                  ].map((req) => (
                    <div key={req.label} className="flex items-center gap-1.5">
                      {req.met ? (
                        <Check className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                      ) : (
                        <X className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                      )}
                      <span className={`text-xs ${req.met ? 'text-green-400' : 'text-gray-500'}`}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`
                    block w-full pl-10 pr-10 py-3 border rounded-lg bg-slate-800/50 text-white
                    placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500
                    ${errors.confirmPassword ? 'border-red-500' : 'border-gray-600 focus:border-orange-500'}
                  `}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Create Account Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-pink-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <UserPlus className="h-5 w-5 mr-2" />
                  Create account
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-800/50 text-gray-400">Or continue with</span>
              </div>
            </div>

            {/* Google Sign Up — rendered by Google Identity Services */}
            <div
              ref={googleBtnRef}
              className="w-full flex justify-center items-center min-h-[44px]"
            />
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-orange-400 hover:text-orange-300 font-medium transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;